import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  Plus, Pencil, Trash2, ShieldCheck, ShieldAlert, AlertTriangle, PauseCircle, ArrowRight,
  File as FileIcon, Info,
} from 'lucide-react';
import api from '../../utils/api';
import { penaltyRate } from '../../utils/penaltyRate';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  SectionCard, Dialog, Field, FormRow, Switch, TableSkeleton, money, fmtDate, fmtTimeRange, plural, num,
} from '../../components/ui';
import StaffHeader from './StaffHeader';
import StaffPayRates from './StaffPayRates';
import ContractorPayProfile from './ContractorPayProfile';
import {
  useStaffRecord, useStableCallback, expiryStatus, expiryText, expirySortKey, ExpiryBadge, IconButton,
  ndisScreening, isScreeningDoc, roleLabel, employmentLabel, genderLabel, docName, docHref, toDay,
} from './hrKit';

const QUAL_STATUS = {
  verified:     { label: 'Verified',     cls: 'badge-green' },
  not_verified: { label: 'Not verified', cls: 'badge-gray' },
  pending:      { label: 'Pending',      cls: 'badge-yellow' },
  expired:      { label: 'Expired',      cls: 'badge-red' },
};

const DAYS = [
  { key: 'monday', label: 'Mon' }, { key: 'tuesday', label: 'Tue' }, { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' }, { key: 'friday', label: 'Fri' }, { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

const none = <span className="text-slate-400">—</span>;
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

function Facts({ items }) {
  return (
    <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
      {items.filter(Boolean).map(({ label, value, wide }) => (
        <div key={label} className={clsx('min-w-0', wide && 'sm:col-span-2')}>
          <dt className="text-[12px] font-medium text-slate-500">{label}</dt>
          <dd className="mt-0.5 break-words text-sm font-medium text-slate-800">{value == null || value === '' ? none : value}</dd>
        </div>
      ))}
    </dl>
  );
}

// One place to change these details: the full edit form, opened at the right section.
function EditLink({ to, label }) {
  return (
    <Link to={to} className="btn-ghost btn-sm" aria-label={label}>
      <Pencil size={12} aria-hidden="true" />Edit
    </Link>
  );
}

/* ── Compliance summary: the first thing on the record ─────────────────────── */

const TONE_TEXT = { danger: 'text-rose-700', warning: 'text-amber-700', success: 'text-emerald-700', neutral: 'text-slate-500' };
const TONE_DOT = { danger: 'bg-rose-500', warning: 'bg-amber-500', success: 'bg-emerald-500', neutral: 'bg-slate-300' };

function buildComplianceItems(staff, docs) {
  const base = `/staff/${staff.id}`;
  const items = [];
  const reqNames = [];

  // Role requirements (what the rostering compliance check uses)
  for (const it of (staff.compliance && staff.compliance.items) || []) {
    reqNames.push(norm(it.qualification_name));
    const upload = `${base}/documents?upload=${encodeURIComponent(it.qualification_name)}`;
    if (!it.held) {
      items.push({ key: `req-${it.qualification_name}`, label: it.qualification_name, tone: 'danger', rank: 0, text: 'Missing', action: { label: 'Upload', to: upload } });
      continue;
    }
    const ex = expiryStatus(it.expiry_date);
    if (ex.state === 'expired') items.push({ key: `req-${it.qualification_name}`, label: it.qualification_name, tone: 'danger', rank: 1, text: expiryText(it.expiry_date), action: { label: 'Upload renewal', to: upload } });
    else if (ex.state === 'soon') items.push({ key: `req-${it.qualification_name}`, label: it.qualification_name, tone: 'warning', rank: 2 + ex.daysLeft / 100, text: expiryText(it.expiry_date), action: { label: 'Upload renewal', to: upload } });
    else items.push({ key: `req-${it.qualification_name}`, label: it.qualification_name, tone: 'success', rank: 9, text: ex.state === 'valid' ? expiryText(it.expiry_date) : 'Current' });
  }

  // NDIS worker screening — clearance record first, then a document on file
  const scr = ndisScreening(staff, docs);
  const screeningTo = `/staff/${staff.id}/edit#screening`;
  const scrItem = { key: 'screening', label: 'NDIS worker screening', tone: scr.tone };
  const scrText = {
    verified: scr.expiry ? expiryText(scr.expiry) : 'Verified',
    expiring: scr.expiry ? expiryText(scr.expiry) : 'Expiring',
    expired: scr.expiry ? expiryText(scr.expiry) : 'Expired',
    invalid: 'Number not valid',
    in_progress: 'In progress',
    document: 'Document on file, not verified',
    document_expired: scr.expiry ? `Document ${expiryText(scr.expiry).toLowerCase()}` : 'Document expired',
    missing: 'Not recorded',
  }[scr.state] || scr.short;
  const scrAction = {
    verified: null,
    expiring: { label: 'Update', to: screeningTo },
    document: { label: 'Verify', to: screeningTo },
    missing: { label: 'Add', to: screeningTo },
  }[scr.state];
  const scrRank = { verified: 9, missing: 8, document: 3, expiring: 2, in_progress: 3 }[scr.state] ?? 1;
  items.push({ ...scrItem, rank: scrRank, text: scrText, action: scrAction === undefined ? { label: 'Update', to: screeningTo } : scrAction });

  // Qualifications on the profile that have run out or are about to, when no role requirement
  // already covers them. Only role requirements were checked before, so a qualification listed as
  // "Verified · Expired 7 July" never reached this box. Per name, the latest record decides: an
  // old expired certificate next to a current renewal is not a problem.
  const latestQual = new Map();
  for (const q of staff.qualifications || []) {
    const k = norm(q.qualification_name);
    if (!k || reqNames.includes(k)) continue;
    const prev = latestQual.get(k);
    const later = !prev || (prev.expiry_date && (!q.expiry_date || String(q.expiry_date) > String(prev.expiry_date)));
    if (later) latestQual.set(k, q);
  }
  const qualLabels = new Set();
  for (const [k, q] of latestQual) {
    const ex = expiryStatus(q.expiry_date);
    if (ex.state !== 'expired' && ex.state !== 'soon') continue;
    qualLabels.add(k);
    items.push({
      key: `qual-${q.id || k}`, label: q.qualification_name, tone: ex.state === 'expired' ? 'danger' : 'warning',
      rank: ex.state === 'expired' ? 1 : 2 + ex.daysLeft / 100, text: expiryText(q.expiry_date),
      action: { label: 'Upload renewal', to: `${base}/documents?upload=${encodeURIComponent(q.qualification_name)}` },
    });
  }

  // Other documents that have run out or are about to (not already covered above)
  for (const d of docs || []) {
    if (isScreeningDoc(d)) continue;
    const ex = expiryStatus(d.expiry_date);
    if (ex.state !== 'expired' && ex.state !== 'soon') continue;
    const n = norm(`${d.category || ''} ${d.name || ''}`);
    const cat = norm(d.category);
    if (reqNames.some((r) => r && (n.includes(r) || (cat.length >= 3 && r.includes(cat))))) continue;
    const label = d.category && d.category !== 'Shared' ? d.category : docName(d);
    // Beside a qualification of the same name, say which one this is ('CPR · Expired 7 Jul' and
    // 'CPR · Document expires in 27 days'), or the two rows read as a contradiction.
    const txt = expiryText(d.expiry_date);
    items.push({
      key: `doc-${d.id}`, label, tone: ex.state === 'expired' ? 'danger' : 'warning', rank: ex.state === 'expired' ? 1 : 2 + ex.daysLeft / 100,
      text: qualLabels.has(norm(label)) ? `Document ${txt.charAt(0).toLowerCase()}${txt.slice(1)}` : txt,
      action: { label: 'Upload renewal', to: `${base}/documents?upload=${encodeURIComponent(label)}` },
    });
  }

  // Employment contract
  const c = staff.latest_contract;
  if (c) {
    const title = c.title || 'Contract';
    const link = `/staff/contracts?staff=${staff.id}`;
    if (c.status === 'signed') items.push({ key: 'contract', label: title, tone: 'success', rank: 9, text: `Signed ${c.signed_at ? fmtDate(c.signed_at) : ''}`.trim(), action: { label: 'View', to: link } });
    else if (c.status === 'sent') items.push({ key: 'contract', label: title, tone: 'warning', rank: 4, text: `Sent ${c.sent_at ? fmtDate(c.sent_at) : ''}, awaiting signature`.replace(' ,', ','), action: { label: 'View', to: link } });
    else if (c.status === 'voided') items.push({ key: 'contract', label: title, tone: 'neutral', rank: 8, text: 'Voided', action: { label: 'View', to: link } });
    else items.push({ key: 'contract', label: title, tone: 'neutral', rank: 8, text: 'Draft', action: { label: 'View', to: link } });
  } else {
    items.push({ key: 'contract', label: 'Employment contract', tone: 'neutral', rank: 8, text: 'None sent', action: { label: 'Send contract', to: `/staff/contracts?send=${staff.id}` } });
  }

  return items.sort((a, b) => a.rank - b.rank);
}

function ComplianceCard({ staff, docs, docsReady }) {
  const [showAll, setShowAll] = useState(false);
  const items = useMemo(() => buildComplianceItems(staff, docs), [staff, docs]);
  const gaps = (staff.compliance && staff.compliance.gaps) || 0;
  const needs = items.filter((i) => i.tone === 'danger' || i.tone === 'warning');
  const hasRequirements = !!(staff.compliance && staff.compliance.items && staff.compliance.items.length);
  const shown = showAll ? items : (needs.length ? needs.slice(0, 3) : []);

  let headline;
  if (gaps > 0) {
    headline = { tone: 'danger', icon: ShieldAlert, text: `Not compliant: ${plural(gaps, 'required check')} missing or expired` };
  } else if (needs.length) {
    headline = { tone: 'warning', icon: AlertTriangle, text: `${plural(needs.length, 'item needs', 'items need')} attention` };
  } else {
    headline = { tone: 'success', icon: ShieldCheck, text: hasRequirements ? 'Compliant: every required check is current' : 'Nothing needs attention' };
  }
  const HeadIcon = headline.icon;

  return (
    <section aria-labelledby="compliance-heading" className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3.5">
        <HeadIcon size={18} className={clsx('shrink-0', TONE_TEXT[headline.tone])} aria-hidden="true" />
        <h2 id="compliance-heading" className={clsx('text-[15px] font-semibold', headline.tone === 'success' ? 'text-slate-900' : TONE_TEXT[headline.tone])}>
          {headline.text}
        </h2>
        {!docsReady && <span className="text-[12px] text-slate-500">Checking documents…</span>}
        <button type="button" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}
          className="ml-auto text-[13px] font-medium text-primary-700 hover:underline">
          {showAll ? 'Show only what needs attention' : `Show all ${items.length}`}
        </button>
      </div>
      {!hasRequirements && (
        <p className="flex items-start gap-2 border-t border-slate-100 px-5 py-2.5 text-[12.5px] text-slate-600">
          <Info size={14} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
          <span>No required checks are set for {roleLabel(staff.role).toLowerCase()}s yet. Set them in <Link to="/settings" className="font-medium text-primary-700 hover:underline">Settings, Compliance requirements</Link>.</span>
        </p>
      )}
      {shown.length > 0 && (
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {shown.map((i) => (
            <li key={i.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-2.5">
              <span className={clsx('h-2 w-2 shrink-0 rounded-full', TONE_DOT[i.tone])} aria-hidden="true" />
              <span className="min-w-0 flex-1 text-sm font-medium text-slate-800">{i.label}</span>
              <span className={clsx('text-[13px]', TONE_TEXT[i.tone] || 'text-slate-600', i.tone === 'success' && '!text-slate-600')}>{i.text}</span>
              {i.action && (
                <Link to={i.action.to} className="inline-flex items-center gap-1 text-[13px] font-medium text-primary-700 hover:underline">
                  {i.action.label}<ArrowRight size={12} aria-hidden="true" />
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ── Qualification dialog ────────────────────────────────────────────────── */

function QualDialog({ open, staffId, qual, onSaved, onClose }) {
  const isEdit = !!qual;
  const [form, setForm] = useState({ qualificationName: '', verificationStatus: 'not_verified', expiryDate: '', issueDate: '', appVisible: true });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setError('');
    setForm(qual ? {
      qualificationName: qual.qualification_name || '',
      verificationStatus: qual.verification_status || 'not_verified',
      expiryDate: qual.expiry_date ? String(qual.expiry_date).slice(0, 10) : '',
      issueDate: qual.issue_date ? String(qual.issue_date).slice(0, 10) : '',
      appVisible: qual.app_visible !== false,
    } : { qualificationName: '', verificationStatus: 'not_verified', expiryDate: '', issueDate: '', appVisible: true });
  }, [open, qual]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const close = useStableCallback(() => { if (!saving) onClose(); });

  async function save(e) {
    e.preventDefault();
    if (!form.qualificationName.trim()) { setError('Enter the qualification name.'); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/staff/${staffId}/qualifications/${qual.id}`, form);
      else await api.post(`/staff/${staffId}/qualifications`, form);
      toast.success(isEdit ? 'Qualification updated' : 'Qualification added');
      setSaving(false);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "The qualification wasn't saved. Try again.");
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={close} title={isEdit ? 'Edit qualification' : 'Add qualification'}
      footer={(
        <>
          <button type="button" onClick={close} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="submit" form="staff-qual-form" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add qualification')}</button>
        </>
      )}>
      <form id="staff-qual-form" onSubmit={save} className="space-y-4">
        <Field label="Qualification" required error={error}>
          {(id) => <input id={id} className="input-field" value={form.qualificationName} onChange={(e) => { set('qualificationName', e.target.value); setError(''); }} placeholder="e.g. First Aid Certificate" />}
        </Field>
        <FormRow>
          <Field label="Status">
            {(id) => (
              <select id={id} className="input-field" value={form.verificationStatus} onChange={(e) => set('verificationStatus', e.target.value)}>
                <option value="not_verified">Not verified</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
                <option value="expired">Expired</option>
              </select>
            )}
          </Field>
          <Field label="Expiry date">
            {(id) => <input id={id} type="date" className="input-field" value={form.expiryDate} onChange={(e) => set('expiryDate', e.target.value)} />}
          </Field>
        </FormRow>
        <Field label="Issue date" hint="For a police check or worker screening with no expiry printed: it then runs 3 years (police) or 5 years (worker screening) from this date.">
          {(id) => <input id={id} type="date" className="input-field" value={form.issueDate} onChange={(e) => set('issueDate', e.target.value)} />}
        </Field>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5">
          <span className="text-sm text-slate-700">Visible in the staff app</span>
          <Switch checked={form.appVisible} onChange={(v) => set('appVisible', v)} label="Visible in the staff app" />
        </div>
      </form>
    </Dialog>
  );
}

/* ── Page ────────────────────────────────────────────────────────────────── */

export default function StaffDetail() {
  const { id } = useParams();
  const confirm = useConfirm();
  const { staff, error, reload } = useStaffRecord(id);
  const [docs, setDocs] = useState([]);
  const [docsReady, setDocsReady] = useState(false);
  const [workAvail, setWorkAvail] = useState(null);
  const [availReady, setAvailReady] = useState(false);
  const [payGroupOptions, setPayGroupOptions] = useState([]);
  const [qualOpen, setQualOpen] = useState(false);
  const [editingQual, setEditingQual] = useState(null);

  useEffect(() => {
    api.get('/pay-groups').then((r) => setPayGroupOptions(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setDocsReady(false);
    setAvailReady(false);
    api.get(`/staff/documents?staffId=${id}&includeExpired=true`)
      .then((r) => setDocs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDocs([]))
      .finally(() => setDocsReady(true));
    api.get(`/staff/availability/${id}`)
      .then((r) => setWorkAvail(r.data || null))
      .catch(() => setWorkAvail(null))
      .finally(() => setAvailReady(true));
  }, [id]);

  const closeQual = useStableCallback(() => { setQualOpen(false); setEditingQual(null); });

  async function deleteQual(q) {
    const ok = await confirm(`${q.qualification_name} will be removed from this staff member's record.`, { title: 'Delete qualification?', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/staff/${id}/qualifications/${q.id}`);
      toast.success('Qualification removed');
      reload();
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't delete the qualification. Try again."); }
  }

  if (!staff) {
    return (
      <div>
        <StaffHeader staff={null} error={error} onRetry={reload} />
        {!error && <div className="rounded-xl border border-slate-200 bg-white"><TableSkeleton rows={5} cols={3} /></div>}
      </div>
    );
  }

  const base = `/staff/${id}`;
  const qualifications = staff.qualifications || [];
  const isContractor = staff.employment_type === 'contractor';
  const pg = payGroupOptions.find((p) => p.name === staff.pay_group);

  const maskBsb = (v) => (v ? (v.length > 3 ? `•••-${v.slice(-3)}` : '•••') : null);
  const maskAccount = (v) => (v ? (v.length > 3 ? `••• ${v.slice(-3)}` : '•••') : null);

  // Pay preview — the same arithmetic the page has always shown, written as money.
  const payPreview = (() => {
    if (isContractor) {
      const wk = parseFloat(staff.contractor_weekday_rate) || 0;
      if (!wk) return null;
      const eff = (v) => { const n = parseFloat(v); return n > 0 ? n : wk; };
      return {
        title: 'Contractor pay per hour (no SCHADS award)',
        rates: [
          { label: 'Weekday', rate: wk },
          { label: 'Saturday', rate: eff(staff.contractor_saturday_rate), fallback: !(parseFloat(staff.contractor_saturday_rate) > 0) },
          { label: 'Sunday', rate: eff(staff.contractor_sunday_rate), fallback: !(parseFloat(staff.contractor_sunday_rate) > 0) },
          { label: 'Public holiday', rate: eff(staff.contractor_ph_rate), fallback: !(parseFloat(staff.contractor_ph_rate) > 0) },
        ],
        note: `* Falls back to the weekday rate. The client is charged from their price book as normal. Super ${staff.contractor_super_applies ? 'is paid' : 'is not paid'}.`,
      };
    }
    const b = parseFloat(staff.override_hourly_rate || (pg && pg.base_hourly_rate) || 0);
    if (!b) return null;
    const satX = parseFloat(pg && pg.saturday_mult) || 1.5;
    const sunX = parseFloat(pg && pg.sunday_mult) || 2.0;
    const phX = parseFloat(pg && pg.ph_mult) || 2.5;
    return {
      title: `Effective hourly rates${staff.override_hourly_rate ? ' (custom rate)' : pg ? `, ${pg.name}` : ''}`,
      rates: [
        { label: 'Weekday', rate: b },
        ...[['Saturday', satX], ['Sunday', sunX], ['Public holiday', phX]].map(([d, x]) => {
          const o = { override: !!staff.override_hourly_rate };
          return { label: d, rate: penaltyRate(b, x, pg, o) };
        }),
      ],
      note: `Base ${money(b)}/hr${staff.override_hourly_rate ? ' (custom)' : ''}. Penalties come from ${pg ? pg.name : 'the award'}.`,
    };
  })();
  const paidRole = ['carer', 'support_worker', 'staff'].includes(staff.role);
  const noPay = paidRole && !payPreview;

  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const shifts = staff.recentShifts || [];
  const upcoming = shifts.filter((s) => String(s.shift_date).slice(0, 10) >= todayKey)
    .sort((a, b) => String(a.shift_date).localeCompare(String(b.shift_date)) || String(a.start_time || '').localeCompare(String(b.start_time || '')));
  const past = shifts.filter((s) => String(s.shift_date).slice(0, 10) < todayKey)
    .sort((a, b) => String(b.shift_date).localeCompare(String(a.shift_date)));
  const shiftDay = (s) => { const d = toDay(s.shift_date); return d ? d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) : '—'; };

  const docsByUrgency = [...docs].sort((a, b) => expirySortKey(a.expiry_date) - expirySortKey(b.expiry_date));

  return (
    <div>
      <StaffHeader staff={staff} error={error} onRetry={reload} />

      <div className="space-y-4">
        {staff.on_hold && (
          <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
            <PauseCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span><b>On hold.</b> This worker is excluded from rostering, and the Scheduling Assistant won't suggest them. Turn this off in Edit when they're ready.</span>
          </div>
        )}

        <ComplianceCard staff={staff} docs={docs} docsReady={docsReady} />

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          {/* Main column */}
          <div className="min-w-0 flex-1 space-y-4">
            <SectionCard title="Personal details" actions={<EditLink to={`${base}/edit#personal`} label="Edit personal details" />} bodyClass="p-5">
              <Facts items={[
                { label: 'First name', value: staff.first_name },
                { label: 'Last name', value: staff.last_name },
                { label: 'Date of birth', value: staff.date_of_birth ? fmtDate(String(staff.date_of_birth).slice(0, 10)) : null },
                { label: 'Gender', value: staff.gender ? genderLabel(staff.gender) : null },
                { label: 'Nationality', value: staff.nationality },
              ]} />
            </SectionCard>

            <SectionCard title="Contact" actions={<EditLink to={`${base}/edit#contact`} label="Edit contact details" />} bodyClass="p-5">
              <Facts items={[
                { label: 'Mobile', value: staff.phone ? <a href={`tel:${staff.phone}`} className="hover:text-primary-700 hover:underline">{staff.phone}</a> : null },
                { label: 'Email', value: staff.email ? <a href={`mailto:${staff.email}`} className="hover:text-primary-700 hover:underline">{staff.email}</a> : null },
                { label: 'Address', value: staff.address, wide: true },
                { label: 'Suburb', value: staff.suburb },
                { label: 'State', value: staff.state },
                { label: 'Postcode', value: staff.postcode },
              ]} />
            </SectionCard>

            <SectionCard title="Employment" actions={<EditLink to={`${base}/edit#role`} label="Edit employment details" />} bodyClass="p-5 space-y-4">
              <Facts items={[
                { label: 'Employee number', value: staff.employee_number },
                { label: 'Role', value: roleLabel(staff.role) },
                { label: 'Employment type', value: staff.employment_type ? employmentLabel(staff.employment_type) : null },
                { label: 'Job title', value: staff.job_title },
                { label: 'Manager', value: staff.manager_name },
                { label: 'Start date', value: staff.start_date ? fmtDate(String(staff.start_date).slice(0, 10)) : null },
                { label: 'End date', value: staff.end_date ? fmtDate(String(staff.end_date).slice(0, 10)) : null },
                ...(isContractor ? [
                  { label: 'Weekday rate', value: staff.contractor_weekday_rate ? `${money(staff.contractor_weekday_rate)}/hr` : null },
                  { label: 'Superannuation', value: staff.contractor_super_applies ? 'Paid (paid mainly for labour)' : 'Not paid' },
                ] : [
                  { label: 'Pay group (SCHADS award)', value: staff.pay_group },
                  { label: 'SCHADS level', value: pg && pg.schads_level },
                  { label: 'Hourly rate', value: (() => { const r = staff.override_hourly_rate || (pg && pg.base_hourly_rate); return r ? `${money(r)}/hr` : null; })() },
                ]),
              ]} />
              {payPreview && (
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
                  <p className="text-[12.5px] font-semibold text-slate-700">{payPreview.title}</p>
                  <dl className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {payPreview.rates.map((r) => (
                      <div key={r.label} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                        <dt className="text-[11.5px] text-slate-500">{r.label}{r.fallback ? ' *' : ''}</dt>
                        <dd className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900">{money(r.rate)}</dd>
                      </div>
                    ))}
                  </dl>
                  <p className="mt-2 text-[11.5px] text-slate-500">{payPreview.note}</p>
                </div>
              )}
              {noPay && (
                <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-700" aria-hidden="true" />
                  <div>
                    <p className="text-[13px] font-semibold text-amber-900">No {isContractor ? 'contractor rate' : 'pay group'} set</p>
                    <p className="mt-0.5 text-[12.5px] leading-relaxed text-amber-800">
                      Approved timesheets for this worker will calculate to $0 pay. <Link to={`${base}/edit#pay`} className="font-medium underline">Set {isContractor ? 'their rate card' : 'a pay group'}</Link>. Invoicing isn't affected: it uses the client's price book.
                    </p>
                  </div>
                </div>
              )}
            </SectionCard>

            {(paidRole || isContractor) && <StaffPayRates userId={id} name={[staff.first_name, staff.last_name].filter(Boolean).join(' ')} />}
            {isContractor && <ContractorPayProfile userId={id} />}

            <SectionCard title="Emergency contact" actions={<EditLink to={`${base}/edit#emergency`} label="Edit emergency contact" />} bodyClass="p-5">
              <Facts items={[
                { label: 'Name', value: staff.emergency_contact_name },
                { label: 'Relationship', value: staff.emergency_contact_relationship },
                { label: 'Phone', value: staff.emergency_contact_phone ? <a href={`tel:${staff.emergency_contact_phone}`} className="hover:text-primary-700 hover:underline">{staff.emergency_contact_phone}</a> : null },
                { label: 'Email', value: staff.emergency_contact_email },
              ]} />
            </SectionCard>

            <SectionCard title="Bank details" actions={<EditLink to={`${base}/edit#tax`} label="Edit bank details" />} bodyClass="p-5">
              <Facts items={[
                { label: 'Account name', value: staff.bank_account_name },
                { label: 'BSB', value: maskBsb(staff.bank_bsb) && <span className="tabular-nums">{maskBsb(staff.bank_bsb)}</span> },
                { label: 'Account number', value: maskAccount(staff.bank_account_number) && <span className="tabular-nums">{maskAccount(staff.bank_account_number)}</span> },
                { label: 'ABN', value: staff.abn && <span className="tabular-nums">{staff.abn}</span> },
              ]} />
              <p className="mt-4 text-[12px] text-slate-500">Bank numbers are masked here. Edit shows them in full.</p>
            </SectionCard>

            <SectionCard title="Work days" actions={<Link to={`${base}/availability`} className="btn-ghost btn-sm"><Pencil size={12} aria-hidden="true" />Edit in Availability</Link>} bodyClass="p-5">
              {!availReady ? <div className="skeleton h-8 w-full" /> : !workAvail ? (
                <p className="text-sm text-slate-500">No work days set. Set them on the Availability tab.</p>
              ) : (
                <ul className="flex flex-wrap gap-2" aria-label="Work days">
                  {DAYS.map(({ key, label }) => {
                    const on = !!workAvail[key];
                    return (
                      <li key={key} className={clsx('inline-flex h-8 min-w-[3rem] items-center justify-center gap-1 rounded-lg border px-2.5 text-[12.5px] font-medium',
                        on ? 'border-slate-300 bg-white text-slate-800' : 'border-slate-200 bg-slate-50 text-slate-400 line-through')}>
                        {label}<span className="sr-only">{on ? ' works' : ' does not work'}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </SectionCard>

            <SectionCard title="Qualifications" actions={(
              <button type="button" onClick={() => { setEditingQual(null); setQualOpen(true); }} className="btn-secondary btn-sm">
                <Plus size={13} aria-hidden="true" />Add
              </button>
            )} bodyClass={qualifications.length ? '' : 'p-5'}>
              {qualifications.length === 0 ? (
                <p className="text-sm text-slate-500">No qualifications recorded. Add one here, or upload a certificate on the Documents tab.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {[...qualifications].sort((a, b) => expirySortKey(a.expiry_date) - expirySortKey(b.expiry_date)).map((q) => {
                    const st = QUAL_STATUS[q.verification_status] || QUAL_STATUS.not_verified;
                    return (
                      <li key={q.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-5 py-3">
                        <span className="min-w-0 flex-1 text-sm font-medium text-slate-800">{q.qualification_name}</span>
                        <span className={st.cls}>{st.label}</span>
                        <ExpiryBadge date={q.expiry_date} />
                        <span className="flex items-center">
                          <IconButton label={`Edit ${q.qualification_name}`} icon={Pencil} size={14} onClick={() => { setEditingQual(q); setQualOpen(true); }} />
                          <IconButton label={`Delete ${q.qualification_name}`} icon={Trash2} size={14} danger onClick={() => deleteQual(q)} />
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </SectionCard>
          </div>

          {/* Side column — stacks under the main column below lg */}
          <div className="w-full shrink-0 space-y-4 lg:w-72">
            <SectionCard title="Teams" actions={<Link to="/teams" className="text-[13px] font-medium text-primary-700 hover:underline">Manage</Link>} bodyClass="p-4">
              {staff.teams && staff.teams.length ? (
                <ul className="flex flex-wrap gap-1.5">
                  {staff.teams.map((t) => (
                    <li key={t.id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[12.5px] text-slate-700">
                      <span className="h-2 w-2 rounded-full" style={{ background: t.color || '#94a3b8' }} aria-hidden="true" />{t.name}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-[13px] text-slate-500">Not in a team.</p>}
            </SectionCard>

            <SectionCard title="Shifts" actions={<Link to={`${base}/employee-shifts`} className="text-[13px] font-medium text-primary-700 hover:underline">View all</Link>} bodyClass="px-4 py-3 space-y-3">
              {[{ title: 'Upcoming', rows: upcoming.slice(0, 4), empty: 'No upcoming shifts.' }, { title: 'Past', rows: past.slice(0, 4), empty: 'No recent shifts.' }].map((grp) => (
                <div key={grp.title}>
                  <h3 className="text-[12px] font-semibold text-slate-500">{grp.title}</h3>
                  {grp.rows.length === 0 ? <p className="mt-1 text-[13px] text-slate-500">{grp.empty}</p> : (
                    <ul className="mt-1 divide-y divide-slate-100">
                      {grp.rows.map((s) => (
                        <li key={s.id} className="py-1.5 text-[13px]">
                          <span className="font-medium text-slate-800">{shiftDay(s)}</span>
                          <span className="text-slate-600"> · {fmtTimeRange(s.start_time, s.end_time)}</span>
                          <span className="block truncate text-[12.5px] text-slate-500">{s.client_names || 'No client'}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </SectionCard>

            <SectionCard title="Documents" actions={<Link to={`${base}/documents`} className="text-[13px] font-medium text-primary-700 hover:underline">View all</Link>} bodyClass="px-4 py-2">
              {!docsReady ? <div className="py-2"><div className="skeleton h-10 w-full" /></div> : docs.length === 0 ? (
                <p className="py-2 text-[13px] text-slate-500">No documents uploaded.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {docsByUrgency.slice(0, 6).map((d) => (
                    <li key={d.id} className="flex items-start gap-2 py-2">
                      <FileIcon size={13} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        {docHref(d)
                          ? <a href={docHref(d)} target="_blank" rel="noreferrer" className="block truncate text-[13px] font-medium text-slate-800 hover:text-primary-700 hover:underline">{docName(d)}</a>
                          : <p className="truncate text-[13px] font-medium text-slate-800">{docName(d)}</p>}
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          {d.category && d.category !== 'Shared' && <span className="text-[12px] text-slate-500">{d.category}</span>}
                          <ExpiryBadge date={d.expiry_date} className="!text-[11.5px]" />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {docs.length > 6 && <p className="pb-2 text-[12px] text-slate-500">{num(docs.length - 6)} more on the Documents tab.</p>}
            </SectionCard>
          </div>
        </div>
      </div>

      <QualDialog open={qualOpen} staffId={id} qual={editingQual} onSaved={reload} onClose={closeQual} />
    </div>
  );
}
